import { mockOverview, mockPlaces } from './mockOverview.js'
import { useLocation } from '../composables/useLocation.js'
import { fetchOverviewLive, fetchCityLookup } from './heweather.js'
import { locale } from '../i18n/index.js'

/**
 * Weather data access layer.
 *
 * In mock mode (default) the module returns a payload with the EXACT shape of the
 * live endpoint:
 *   GET https://assets.OPERA.cn/service/weather/overview
 *     -> responses[0].weather[0] : { current, forecast, alerts, nowcasting, … }
 *
 * Flip VITE_OPERA_LIVE=true and set VITE_OPERA_API_BASE + VITE_OPERA_API_KEY to hit the
 * real service. The key is intentionally NOT bundled; supply it via a reverse proxy.
 */
const LIVE = import.meta.env.VITE_OPERA_LIVE === 'true'
const API_BASE = import.meta.env.VITE_OPERA_API_BASE || 'https://assets.OPERA.cn'
const API_KEY = import.meta.env.VITE_OPERA_API_KEY || ''
const LATENCY = 320 // simulated network latency for the mock

/** Data source selector. 'mock' (default) | 'OPERA' (legacy OPERA live) | 'heweather' (operapps Next API). */
const SOURCE = import.meta.env.VITE_WEATHER_SOURCE || 'mock'

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

function buildOverviewQuery({ lat, lon }) {
  const p = new URLSearchParams({
    apikey: API_KEY,
    cm: 'zh-cn', it: 'web', scn: 'ANON',
    // ALWAYS ask the API for metric/Celsius. The °C/°F switch is a pure
    // presentation concern handled by useUnits().toDisplay() — requesting
    // Fahrenheit here would mean the temperature unit change triggers a brand
    // new network round-trip (and a loading flash) for data we already have.
    units: 'C',
    appId: '9e21380c-ff19-4c78-b4ea-19558e93a5d3',
    wrapodata: 'false', includemapsmetadata: 'true', cuthour: 'true',
    lifeDays: '2', lifeModes: '17', includestorm: 'true',
    includeLifeActivity: 'true', lifeSubTypes: '1,2,3,10,24,26',
    includeActivity: 'true', includeUserPreference: 'true',
    insights: '65536', insightCategories: 'Temperature,SkyCondition',
    startDate: '-1', endDate: '+0', discardFutureInsightTimeseries: 'true',
    distanceinkm: '0', regionDataCount: '20', orderby: 'distance', days: '10',
    pageOcid: 'prime-weather::weathertoday-peregrine', source: 'weather_csr',
    region: 'cn', market: 'zh-cn', locale: 'zh-cn',
    lat: String(lat), lon: String(lon)
  })
  return `${API_BASE}/service/weather/overview?${p.toString()}`
}

export async function fetchOverview({ lat, lon, lang }) {
  // Every weather interface goes through locHandle(lat, lon). Prefer the caller's
  // FRONT-END coordinates; when they are missing / non-finite we fall back to
  // "0,0" so no interface is ever called with a stale or invented location.
  const la = Number.isFinite(Number(lat)) ? Number(lat) : 0
  const lo = Number.isFinite(Number(lon)) ? Number(lon) : 0
  if (SOURCE === 'heweather') {
    try {
      return await fetchOverviewLive({ lat: la, lon: lo, lang: lang || locale.value || 'zh-CN' })
    } catch (e) {
      console.warn('[weather] HeWeather live fetch failed, falling back to mock:', e)
      const clone = structuredClone(mockOverview)
      clone.responses[0].source.coordinates = { lat: la, lon: lo }
      return clone
    }
  }
  if (!LIVE) {
    await sleep(LATENCY)
    // Localise the mock to the requested point without changing the response shape.
    const clone = structuredClone(mockOverview)
    clone.responses[0].source.coordinates = { lat: la, lon: lo }
    return clone
  }
  const res = await fetch(buildOverviewQuery({ lat: la, lon: lo }), {
    headers: { Accept: 'application/json' },
    credentials: 'omit'
  })
  if (!res.ok) throw new Error(`overview ${res.status}`)
  return res.json()
}

export async function fetchAutoSuggest(q, { lat = 0, lon = 0 } = {}) {
  if (SOURCE === 'heweather') {
    try {
      return await fetchCityLookup(q, locale.value)
    } catch (e) {
      console.warn('[weather] HeWeather city lookup failed, falling back to mock:', e)
      const needle = q.trim()
      return needle
        ? mockPlaces.filter((p) => p.name.includes(needle) || p.region.includes(needle))
        : mockPlaces.slice(0, 5)
    }
  }
  if (!LIVE) {
    await sleep(120)
    const needle = q.trim()
    return needle
      ? mockPlaces.filter((p) => p.name.includes(needle) || p.region.includes(needle))
      : mockPlaces.slice(0, 5)
  }
  const p = new URLSearchParams({
    appid: 'EDEC3CB74CF190BBBE26DF7938F3D961E925F593',
    count: '5', q,
    localcircularview: `${Math.round(lat)},${Math.round(lon)},100`,
    setmkt: 'zh-cn', setlang: 'zh-cn',
    types: 'Place,Address,Business',
    abbrtext: '1', structuredaddress: 'true', strucaddrread: '1'
  })
  const res = await fetch(`${API_BASE}/service/weather/api/v6/Places/AutoSuggest?${p}`, {
    headers: { Accept: 'application/json' }
  })
  if (!res.ok) throw new Error(`autosuggest ${res.status}`)
  const json = await res.json()
  return (json?.value ?? []).map((v) => ({
    id: v.id,
    name: v.address?.formattedAddress ?? v.name,
    region: v.address?.adminDistrict ?? '',
    country: v.address?.countryRegionIso2 ?? 'CN',
    lat: v.point?.coordinates?.[0],
    lon: v.point?.coordinates?.[1],
    type: v.type
  }))
}

/** Convenience wrapper reading the current location from composables.
 *  The unit is deliberately NOT sent: the payload is always metric and the
 *  °C/°F switch happens in the view layer. */
export async function refresh() {
  const { activeLocation } = useLocation()
  const loc = activeLocation.value
  return fetchOverview({ lat: loc.lat, lon: loc.lon })
}
